// ── 미팅 목록 뷰 컴포넌트 ────────────────────────────────────────────────────
// - 카드 형태로 미팅 목록을 표시
// - 미결과 항목: 연필 아이콘 버튼 / 계약완료: 자물쇠 아이콘
// - 좌표 없는 항목: 경고 텍스트 표시

import React from 'react';
import { CopyAddressButton } from '../../../components/meetingmap/CopyAddressButton';
import { PhoneValue } from '../../../components/meetingmap/PhoneValue';
import { PencilIcon } from '../../../components/meetingmap/Icons';
import {
  ticketLabel, TXT,
  parseCoord, isMapDisplayCoord,
} from '../../../components/meetingmap/utils';
import {
  listWrapStyle, cardStyle, sheetTopRowStyle,
  addressRowStyle, phoneInlineStyle, contentRowStyle, resultChipRowStyle,
} from '../../../components/meetingmap/styles';
// 글자 크기(보통/크게)에 따라 목록 전용 스타일 한 벌을 만들어 쓴다 (2026-09-11).
//   공유 스타일을 복사해 크기만 덮으므로 바텀시트(마커 창)는 영향받지 않는다.
import { buildListStyles } from '../../../components/meetingmap/stylesListScale';

export function MeetingListView({
  loading,
  meetings,
  listWrapRef,
  view,           // 'list' 일 때만 grid 표시
  onResultOpen,   // 결과 입력 모달 열기 콜백
  showToast,
  textSize = 'normal',   // 'normal' | 'large' — 글자 크게 보기 (2026-09-11)
}) {
  const st = buildListStyles(textSize);
  return (
    <div
      ref={listWrapRef}
      style={{ ...listWrapStyle, display: view === 'list' ? 'grid' : 'none' }}
    >
      {loading && <div style={st.empty}>{TXT.loading}</div>}
      {/* 0건일 때 "없다"에서 끝내지 않는다 — 다음에 무엇을 하면 되는지 알려준다 (2026-09-09) */}
      {!loading && meetings.length === 0 && (
        <div style={st.empty}>
          {TXT.empty}
          <div style={st.hint}>
            위의 날짜를 눌러 다른 날짜를 볼 수 있습니다.
          </div>
        </div>
      )}

      {!loading && meetings.map((item) => {
        const lat = parseCoord(item.latitude);
        const lng = parseCoord(item.longitude);
        const hasCoord = lat !== null && lng !== null && isMapDisplayCoord(lat, lng);
        // 미뤄짐·미팅인계는 미표시 — 계약완료·킵·기타 피드백만 결과 있음으로 처리
        const hasResult = Boolean(item.is_contracted || item.is_keep || item.feedback);

        return (
          <div key={item.id} style={cardStyle}>
            {/* ── 상호명 + 상품뱃지 + 연필/잠금 아이콘 ── */}
            <div style={sheetTopRowStyle}>
              <div style={{ minWidth: 0 }}>
                <div style={st.company}>{item.company_name || '-'}</div>
                <div style={st.sub}>{item.ceo_name || '-'} &middot; {item.visit_time || '-'}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                <span style={st.ticket}>{ticketLabel(item.contract_items, item.ticket_type)}</span>
                {item.is_contracted ? (
                  <span
                    style={st.lockedIcon}
                    title={item.contract_status === 'cancelled'
                      ? '계약취소 (수정 불가)'
                      : '계약 접수 완료 (수정 불가)'}
                  >🔒</span>
                ) : (
                  <button
                    type="button"
                    style={st.resultIconBtn(hasResult, item.feedback_color_key)}
                    title={hasResult ? TXT.resultEdit : TXT.resultInput}
                    onClick={() => onResultOpen(item)}
                  >
                    <PencilIcon size={st.iconSize} />
                  </button>
                )}
              </div>
            </div>

            {/* ── 주소 ── */}
            <div style={addressRowStyle}>
              <div style={st.address}>{item.address || TXT.noAddress}</div>
              <CopyAddressButton
                address={item.address}
                onError={() => showToast('복사에 실패했습니다.', 'error')}
                iconSize={st.iconSize}
                textSize={st.copyTextSize}
              />
            </div>

            {/* ── 전화번호 ── */}
            <div style={phoneInlineStyle}>
              <span style={st.phoneParen}>(</span>
              <PhoneValue value={item.phone} linkStyle={st.phoneLink} emptyStyle={st.phoneEmpty} />
              <span style={st.phoneSlash}> / </span>
              <PhoneValue value={item.mobile_phone} linkStyle={st.phoneLink} emptyStyle={st.phoneEmpty} />
              <span style={st.phoneParen}>)</span>
            </div>

            {/* ── 내용 ── */}
            <div style={contentRowStyle}>
              <span style={st.contentLabel}>{TXT.content}</span>
              <div style={st.contentValue}>{String(item.content || '').trim() || '-'}</div>
            </div>

            {/* ── 좌표 없음 경고 ── */}
            {!hasCoord && <div style={st.warn}>{TXT.noCoord}</div>}

            {/* ── 결과 chip ── */}
            {hasResult && (
              <div style={resultChipRowStyle}>
                {item.is_contracted && (
                  item.contract_status === 'cancelled'
                    ? <span style={st.cancelledChip}>계약취소</span>
                    : <span style={st.contractedChip}>✓ 계약완료</span>
                )}
                {item.is_keep
                  ? <span style={st.keepChip}>☆ 킵</span>
                  : item.feedback && !item.is_contracted && (
                      <span style={st.feedbackChip(item.feedback_color_key)}>{item.feedback}</span>
                    )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
